import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../api/supabase'

export default function AuthCallback() {
  const navigate = useNavigate()
  const [status, setStatus] = useState('Processing login...')

  useEffect(() => {
    let redirected = false
    const doRedirect = (path: string) => {
      if (!redirected) { redirected = true; navigate(path, { replace: true }) }
    }

    const handleAuth = async () => {
      try {
        // Check for errors in query params
        const params = new URLSearchParams(window.location.search)
        if (params.get('error')) {
          setStatus('Login failed: ' + params.get('error'))
          setTimeout(() => doRedirect('/login'), 3000)
          return
        }

        // Case 1: Implicit flow — tokens in URL hash
        const hash = window.location.hash
        if (hash && hash.includes('access_token=')) {
          const hashParams = new URLSearchParams(hash.substring(1))
          const access_token = hashParams.get('access_token')
          const refresh_token = hashParams.get('refresh_token')
          if (access_token && refresh_token) {
            const { data, error } = await supabase.auth.setSession({ access_token, refresh_token })
            if (error) {
              setStatus('Session error: ' + error.message)
              setTimeout(() => doRedirect('/login'), 3000)
              return
            }
            if (data.session) {
              setStatus('Login successful! Redirecting...')
              doRedirect('/jobs')
              return
            }
          }
        }

        // Case 2: PKCE flow — code in query params (Supabase handles exchange automatically)
        // Just wait for onAuthStateChange to fire
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
          if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && session) {
            setStatus('Login successful! Redirecting...')
            doRedirect('/jobs')
          }
        })

        // Also check existing session
        const { data: { session } } = await supabase.auth.getSession()
        if (session) {
          setStatus('Login successful! Redirecting...')
          doRedirect('/jobs')
          return
        }

        // Fallback timeout
        setTimeout(() => {
          if (!redirected) {
            setStatus('Session not found. Redirecting to login...')
            doRedirect('/login')
          }
        }, 10000)

        return () => subscription.unsubscribe()
      } catch (err) {
        setStatus('Error: ' + String(err))
        setTimeout(() => doRedirect('/login'), 3000)
      }
    }

    handleAuth()
  }, [navigate])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
        <p className="text-gray-600 dark:text-gray-400">{status}</p>
      </div>
    </div>
  )
}
